<template>
    <div class="lms__superadmin_users_edit_form">
        <form-text
            :label="$t('lms.superadmin.labels.email')"
            :disabled="isEditMode || isShowMode"
            :name="'admin-email'"
            :required="true"
            :v-validate="'required|email'"
            :data-vv-as="$t('forms.dataVvAs.email')"
            v-model="userEmail"
        />
        <form-text
            :label="$t('lms.superadmin.labels.firstName')"
            :disabled="disableUserFormFields || isShowMode || (!userEmail && !userFirstName)"
            :name="'admin-first-name'"
            :required="true"
            :v-validate="'required|max:45'"
            :data-vv-as="$t('forms.dataVvAs.firstName')"
            v-model="userFirstName"
        />
        <form-text
            :label="$t('lms.superadmin.labels.lastName')"
            :disabled="disableUserFormFields || isShowMode || (!userEmail && !userFirstName)"
            :name="'admin-last-name'"
            :required="true"
            :v-validate="'required|max:45'"
            :data-vv-as="$t('forms.dataVvAs.lastName')"
            v-model="userLastName"
        />
        <template v-if="isShowMode || isEditMode">
            <div class="columns">
                <template v-if="user.isSuperadmin()">
                    <div class="column is-narrow">
                        <component-button
                            @click="openResetMfaNotify"
                            :theme="'default'"
                            :name="'lms-superadmin-admin-edit-form-reset-mfa'"
                        >{{ $t('lms.superadmin.buttons.resetMFA') }}
                        </component-button>
                    </div>
                </template>
                <div class="column is-narrow">
                    <component-button
                        @click="openResetPasswordNotify"
                        :theme="'default'"
                        :name="'lms-superadmin-admin-edit-form-reset-password'"
                    >{{ $t('lms.superadmin.buttons.resetPassword') }}
                    </component-button>
                </div>
                <div class="column"></div>
            </div>
        </template>
        <pop-notify
            :class="'pop__notify--resetmfa'"
            ref="popnotifyresetmfa"
            :title-text="$t('lms.superadmin.popNotify.resetMFA')"
            :content-text="$t('lms.superadmin.popNotify.textMFA')"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-danger="true"
            :submit-button-text="$t('lms.superadmin.popNotify.resetMFA')"
            :prevent-close="true"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'reset-mfa'"
            @emit-submit="resetMfa"
        />
        <pop-notify
            :class="'pop__notify--resetpassword'"
            ref="popnotifyresetpassword"
            :title-text="$t('lms.superadmin.popNotify.resetPassword')"
            :content-text="$t('lms.superadmin.popNotify.textPassword')"
            :cancel-button-text="$t('lms.superadmin.popNotify.cancel')"
            :submit-button-danger="true"
            :submit-button-text="$t('lms.superadmin.popNotify.resetPassword')"
            :prevent-close="true"
            :submit-button-animate-loading="true"
            :submit-button-animate-loading-name="'reset-password'"
            @emit-submit="resetPassword"
        />
        <pop-notify
            :class="'pop__notify--validation'"
            ref="popnotifyvalidation"
            :title-text="`Something's not quite right`"
            :content-text="'Please check that you have filled out all the fields in the form correctly and try again.'"
            :cancel-button-text="'Ok'"
            :submit-button-active="false"
        />
        <pop-notify
            :class="'pop__notify--resend'"
            ref="notifyresendinvitation"
            :title-text="$t('lms.superadmin.popNotify.resendInvitation')"
            :content-text="$t('lms.superadmin.popNotify.contentTextInvitation')"
            :cancel-button-text="$t('lms.superadmin.popNotify.dismissButton')"
            :submit-button-text="$t('lms.superadmin.popNotify.confirmButton')"
            :submit-button-danger="true"
            :prevent-close="true"
        />
    </div>
</template>

<script>
import FormText from '@/components/default/forms/FormText'
import PopNotify from '@/components/default/shared/PopNotify'
import ComponentButton from '@/components/default/shared/ComponentButton'
import { validateBeforeRouteLeave, watchContentChangeEmit } from '@/helpers/mixins'
import { User } from '@/User'

export default {
  name: 'lms-superadmin-admin-edit-form',
  components: {
    ComponentButton,
    PopNotify,
    FormText
  },
  mixins: [validateBeforeRouteLeave, watchContentChangeEmit],
  data () {
    return {
      user: new User(),
      disableUserFormFields: false,
      userExists: false
    }
  },
  computed: {
    userId () {
      return this.$store.getters.getLmsSuperadminUserId
    },
    userEmail: {
      get () {
        return this.$store.getters.getLmsSuperadminUserEmail
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserSetEmail', value)
      }
    },
    userFirstName: {
      get () {
        return this.$store.getters.getLmsSuperadminUserFirstName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserSetFirstName', value)
      }
    },
    userLastName: {
      get () {
        return this.$store.getters.getLmsSuperadminUserLastName
      },
      set (value) {
        this.$store.dispatch('lmsSuperadminUserSetLastName', value)
      }
    },
    //
    watchContent () {
      return this.userEmail + this.userFirstName + this.userLastName
    },
    //
    isAddMode () {
      return this.$route.name.includes('user-add')
    },
    isEditMode () {
      return this.$route.name.includes('user-edit')
    },
    isShowMode () {
      return this.$route.name.includes('user-show')
    },
    isStatusVisible () {
      return this.userStatus === 'active' || this.userStatus === 'inactive' || this.userStatus === 'expired'
    }
  },
  methods: {
    openResetMfaNotify () {
      this.$refs.popnotifyresetmfa.openNotify()
    },
    openResetPasswordNotify () {
      this.$refs.popnotifyresetpassword.openNotify()
    },
    resetMfa () {
      this.$store.dispatch('authAdminMfaReset', this.userId).finally(() => {
        this.$eventBus.$emit('stop-reset-mfa')
        this.$refs.popnotifyresetmfa.closeNotify()
      })
    },
    resetPassword () {
      this.$store.dispatch('authAdminPasswordReset', this.userId).finally(() => {
        this.$eventBus.$emit('stop-reset-password')
        this.$refs.popnotifyresetpassword.closeNotify()
      })
    }
  }
}
</script>
